import { FileJson, FlaskConical, Link2, Loader2, RefreshCw, Search, Upload } from 'lucide-react';
import { type ChangeEvent, type DragEvent, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraftOperation } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  bindingLabel,
  bindingsText,
  dataDescriptionText,
  errorMessage,
  parseBindingsInput,
  parseDataDescriptionInput,
  type StepProps,
} from '../builderModel';
import { Field, StepFrame } from '../builderUi';
import { DataDescriptionSection } from './DataDescriptionSection';
import { TestOperationDialog } from './TestOperationDialog';

const METHOD_CLASSES: Record<string, string> = {
  GET: 'text-success',
  POST: 'text-sky-400',
  PUT: 'text-warning',
  PATCH: 'text-warning',
  DELETE: 'text-destructive',
};

function SpecImporter({ draftId, onImported, compact }: { draftId: string; onImported: StepProps['replace']; compact?: boolean }) {
  const [spec, setSpec] = useState('');
  const [fileName, setFileName] = useState<string>();
  const [importing, setImporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('The spec must be under 2 MB.');
      return;
    }
    setSpec(await file.text());
    setFileName(file.name);
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    void loadFile(event.dataTransfer.files[0]);
  };

  const importSpec = async () => {
    setImporting(true);
    try {
      const draft = await adminClient.importIntegrationDraftSpec(draftId, spec);
      onImported(draft);
      setSpec('');
      setFileName(undefined);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <label
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 text-center transition-colors',
          compact ? 'py-6' : 'py-10',
          dragging ? 'border-brand bg-brand-muted' : 'border-border hover:border-muted-foreground/50 hover:bg-muted/30',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".json,.yaml,.yml,application/json,application/yaml,text/yaml"
          className="sr-only"
          onChange={(event: ChangeEvent<HTMLInputElement>) => void loadFile(event.target.files?.[0])}
        />
        {fileName ? <FileJson className="h-6 w-6 text-brand" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
        <div className="text-sm font-medium">{fileName ?? 'Drop an OpenAPI file here, or click to browse'}</div>
        <div className="text-xs text-muted-foreground">OpenAPI 3.x or Swagger 2.0 · JSON or YAML · one bundled file, up to 2 MB</div>
      </label>
      <Field id="spec-paste" label="Or paste the spec" optional>
        <Textarea
          id="spec-paste"
          value={spec}
          onChange={(event) => {
            setSpec(event.target.value);
            setFileName(undefined);
          }}
          placeholder={'openapi: 3.0.3\ninfo:\n  title: Acme Directory\n  version: 1.0.0\npaths:\n  /people:\n    get:\n      operationId: listPeople'}
          className="min-h-40 font-mono text-xs"
          spellCheck={false}
        />
      </Field>
      <div className="flex justify-end">
        <Button onClick={() => void importSpec()} disabled={!spec.trim() || importing}>
          {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Import contract
        </Button>
      </div>
    </div>
  );
}

const BINDINGS_EXAMPLE = '[{ "in": "body", "path": ["filter", "content.coveringRealmIds"], "setting": "jurisdiction" }]';

/**
 * The operation's bindings: parameters Studio fills itself, from a value RapidSOS
 * sets per organization or a fixed one, whatever the app sends (proposal 0204).
 */
function BindingsEditor({
  operation,
  readOnly,
  onChange,
}: {
  operation: IntegrationDraftOperation;
  readOnly: boolean;
  onChange: (next: IntegrationDraftOperation) => void;
}) {
  const [text, setText] = useState(() => bindingsText(operation.bindings));
  const [problem, setProblem] = useState<string>();
  const apply = (value: string) => {
    const parsed = parseBindingsInput(value);
    if ('error' in parsed) {
      setProblem(parsed.error);
      return;
    }
    setProblem(undefined);
    const { bindings: _previous, ...rest } = operation;
    onChange(parsed.value.length ? { ...rest, bindings: parsed.value } : rest);
  };
  return (
    <tr className="border-t border-border/50 bg-card/30">
      <td />
      <td colSpan={3} className="space-y-2 py-3 pl-2 pr-4">
        <p className="text-xs text-muted-foreground">
          Bound parameters are filled by Studio on every call, replacing what the app sent. A setting takes the value RapidSOS sets for each
          organization in Studio Admin; until it is set, that organization&apos;s calls are refused.
        </p>
        <Textarea
          aria-label={`Bindings for ${operation.operationId}`}
          value={text}
          readOnly={readOnly}
          onChange={(event) => setText(event.target.value)}
          onBlur={() => apply(text)}
          placeholder={BINDINGS_EXAMPLE}
          className="min-h-[88px] font-mono text-xs"
        />
        {problem ? <p className="text-xs text-destructive">{problem}</p> : null}
      </td>
    </tr>
  );
}

function OperationRow({
  operation,
  readOnly,
  onChange,
  onTest,
}: {
  operation: IntegrationDraftOperation;
  readOnly: boolean;
  onChange: (next: IntegrationDraftOperation) => void;
  onTest: () => void;
}) {
  const [editingBindings, setEditingBindings] = useState(false);
  const bindings = operation.bindings ?? [];
  return (
    <>
    <tr className={cn('border-t border-border', !operation.enabled && 'opacity-50')}>
      <td className="py-2.5 pl-4 pr-2">
        <input
          type="checkbox"
          aria-label={`Enable ${operation.operationId}`}
          checked={operation.enabled}
          disabled={readOnly}
          onChange={(event) => onChange({ ...operation, enabled: event.target.checked })}
          className="h-4 w-4 cursor-pointer accent-[var(--brand)]"
        />
      </td>
      <td className="px-2 py-2.5">
        <span className={cn('font-mono text-[11px] font-semibold', METHOD_CLASSES[operation.method] ?? 'text-muted-foreground')}>
          {operation.method}
        </span>
      </td>
      <td className="min-w-0 px-2 py-2.5">
        <div className="truncate font-mono text-xs">{operation.path}</div>
        <div className="truncate text-xs text-muted-foreground">
          {operation.summary || operation.operationId}
        </div>
        {bindings.length ? (
          <div className="mt-1 space-y-0.5">
            {bindings.map((binding, index) => (
              <div key={index} className="truncate font-mono text-[11px] text-sky-400" title="Filled by Studio, never by the app">
                {bindingLabel(binding)}
              </div>
            ))}
          </div>
        ) : null}
      </td>
      <td className="py-2.5 pl-2 pr-4 text-right">
        <button
          type="button"
          onClick={onTest}
          title="Call it for real with the stored credentials"
          className="mr-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 align-middle text-[11px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <FlaskConical className="h-3 w-3" />
          Test
        </button>
        <button
          type="button"
          onClick={() => setEditingBindings((open) => !open)}
          aria-expanded={editingBindings}
          title="Parameters Studio fills itself"
          className={cn(
            'mr-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 align-middle text-[11px] transition-colors hover:text-foreground',
            bindings.length ? 'text-sky-400' : 'text-muted-foreground',
          )}
        >
          <Link2 className="h-3 w-3" />
          {bindings.length ? `Bound (${bindings.length})` : 'Bind'}
        </button>
        <div className="inline-flex rounded-md border border-border p-0.5 align-middle text-[11px]">
          {(['read', 'write'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              disabled={readOnly}
              onClick={() => onChange({ ...operation, kind })}
              className={cn(
                'rounded px-2 py-0.5 capitalize transition-colors',
                operation.kind === kind ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {kind}
            </button>
          ))}
        </div>
      </td>
    </tr>
    {editingBindings ? <BindingsEditor operation={operation} readOnly={readOnly} onChange={onChange} /> : null}
    </>
  );
}

export function ContractStep({ draft, readOnly, saving, save, replace, next }: StepProps) {
  const contract = draft.contract;
  const [baseUrl, setBaseUrl] = useState(contract?.baseUrl ?? '');
  const [operations, setOperations] = useState(contract?.operations ?? []);
  const [query, setQuery] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [syncedSha, setSyncedSha] = useState(contract?.specSha256);
  const [testing, setTesting] = useState<IntegrationDraftOperation>();
  // The data description editor: text, synced to the saved value whenever the server's copy changes.
  const savedDescription = dataDescriptionText(draft.dataDescription);
  const [descriptionText, setDescriptionText] = useState(savedDescription);
  const [syncedDescription, setSyncedDescription] = useState(savedDescription);

  if (contract && contract.specSha256 !== syncedSha) {
    setSyncedSha(contract.specSha256);
    setBaseUrl(contract.baseUrl);
    setOperations(contract.operations);
    setReplacing(false);
  }
  if (savedDescription !== syncedDescription) {
    setSyncedDescription(savedDescription);
    setDescriptionText(savedDescription);
  }
  const descriptionDirty = descriptionText.trim() !== savedDescription.trim();

  const dirty =
    Boolean(contract) &&
    (baseUrl !== contract?.baseUrl ||
      descriptionDirty ||
      operations.some((operation, index) => {
        const saved = contract?.operations[index];
        return (
          saved?.enabled !== operation.enabled ||
          saved?.kind !== operation.kind ||
          JSON.stringify(saved?.bindings ?? []) !== JSON.stringify(operation.bindings ?? [])
        );
      }));

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return operations;
    return operations.filter((operation) =>
      [operation.path, operation.operationId, operation.summary, operation.method, ...operation.tags].some((value) =>
        value.toLowerCase().includes(needle),
      ),
    );
  }, [operations, query]);

  const persist = async () => {
    const description = parseDataDescriptionInput(descriptionText);
    if ('error' in description) {
      toast.error(description.error);
      return undefined;
    }
    return save({
      baseUrl,
      // Bindings go out as they stand, `[]` included, so removing the last one clears it on the server.
      operations: operations.map(({ operationId, kind, enabled, rateId, bindings }) => ({
        operationId,
        kind,
        enabled,
        ...(rateId ? { rateId } : {}),
        bindings: bindings ?? [],
      })),
      // Sent only when it changed: the saved one stays as it is otherwise.
      ...(descriptionDirty ? { dataDescription: description.value } : {}),
    });
  };

  const enabledCount = operations.filter((operation) => operation.enabled).length;
  const writeCount = operations.filter((operation) => operation.enabled && operation.kind === 'write').length;

  return (
    <StepFrame
      title="API contract"
      description={
        <>
          Start from the service's OpenAPI description. Studio reads the operations, servers and security schemes from it —
          nothing is fetched from the internet, so bundle any external <code className="font-mono text-xs">$ref</code>s into one file first.
        </>
      }
      dirty={dirty}
      saving={saving}
      readOnly={readOnly || !contract}
      onSave={() => void persist()}
      onContinue={async () => {
        if (!dirty || (await persist())) next();
      }}
    >
      {!contract || replacing ? (
        <div className="rounded-xl border border-border bg-card/40 p-5">
          <SpecImporter draftId={draft.draftId} onImported={replace} compact={Boolean(contract)} />
          {contract ? (
            <div className="mt-3 flex justify-start">
              <Button variant="ghost" size="sm" onClick={() => setReplacing(false)}>
                Keep the current contract
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="grid gap-4 rounded-xl border border-border bg-card/40 p-5 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <div className="text-xs text-muted-foreground">Contract</div>
              <div className="mt-1 truncate text-sm font-semibold">{contract.title || 'Untitled API'}</div>
              <div className="text-xs text-muted-foreground">
                v{contract.version || '—'} · OpenAPI {contract.openapiVersion} · {contract.specFormat.toUpperCase()}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Operations</div>
              <div className="mt-1 font-mono text-sm">
                {enabledCount}/{operations.length} <span className="text-xs text-muted-foreground">enabled</span>
              </div>
              <div className="text-xs text-muted-foreground">{writeCount} with side effects</div>
            </div>
            <div className="flex flex-col items-start justify-between gap-2 sm:items-end">
              <span className="font-mono text-[11px] text-muted-foreground" title={contract.specSha256}>
                sha256 {contract.specSha256.slice(0, 12)}
              </span>
              {readOnly ? null : (
                <Button variant="outline" size="sm" onClick={() => setReplacing(true)}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Replace spec
                </Button>
              )}
            </div>
          </div>

          <Field
            id="base-url"
            label="Base URL"
            hint={
              contract.servers.length
                ? 'Every request goes to this host. Only https servers from the spec are offered.'
                : 'The spec declared no https server. Enter the production base URL.'
            }
          >
            <div className="flex flex-wrap gap-2">
              {contract.servers.map((server) => (
                <button
                  key={server}
                  type="button"
                  disabled={readOnly}
                  onClick={() => setBaseUrl(server)}
                  className={cn(
                    'rounded-md border px-2.5 py-1 font-mono text-xs transition-colors',
                    baseUrl === server ? 'border-brand bg-brand-muted text-foreground' : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {server}
                </button>
              ))}
            </div>
            <Input
              id="base-url"
              value={baseUrl}
              disabled={readOnly}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="https://api.example.com/v1"
              className="font-mono text-sm"
            />
          </Field>

          <div className="overflow-hidden rounded-xl border border-border">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-card/40 px-4 py-3">
              <div>
                <div className="text-sm font-semibold">Operations</div>
                <div className="text-xs text-muted-foreground">
                  Enable what apps may call. Mark anything that changes data as <span className="text-foreground">write</span> — those need
                  an idempotency story in review.
                </div>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter operations" className="h-9 pl-8 text-xs" />
              </div>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-12" />
                  <col className="w-20" />
                  <col />
                  <col className="w-52" />
                </colgroup>
                <tbody>
                  {visible.map((operation) => (
                    <OperationRow
                      key={operation.operationId}
                      operation={operation}
                      readOnly={readOnly}
                      onChange={(changed) =>
                        setOperations((current) => current.map((item) => (item.operationId === changed.operationId ? changed : item)))
                      }
                      onTest={() => setTesting(operation)}
                    />
                  ))}
                </tbody>
              </table>
              {visible.length === 0 ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">No operations match.</div> : null}
            </div>
          </div>

          <DataDescriptionSection text={descriptionText} saved={draft.dataDescription} readOnly={readOnly} onChange={setDescriptionText} />
        </>
      )}
      <TestOperationDialog key={testing?.operationId} draft={draft} operation={testing} onClose={() => setTesting(undefined)} />
    </StepFrame>
  );
}
